export class SseParser {
    constructor(onEvent, onId = () => {}, onRetry = () => {}) {
        this.onEvent = onEvent;
        this.onId = onId;
        this.onRetry = onRetry;
        this.buffer = '';
        this.event = '';
        this.data = [];
    }

    push(chunk) {
        this.buffer += chunk;
        while (true) {
            const end = this.buffer.search(/[\r\n]/);
            if (end < 0 || (this.buffer[end] === '\r' && end === this.buffer.length - 1)) return;
            const line = this.buffer.slice(0, end);
            const length = this.buffer[end] === '\r' && this.buffer[end + 1] === '\n' ? 2 : 1;
            this.buffer = this.buffer.slice(end + length);
            this.line(line);
        }
    }

    line(line) {
        if (line === '') {
            if (this.data.length) this.onEvent({ event: this.event || 'message', data: this.data.join('\n') });
            this.event = '';
            this.data = [];
            return;
        }
        if (line.startsWith(':')) return;
        const colon = line.indexOf(':');
        const field = colon < 0 ? line : line.slice(0, colon);
        let value = colon < 0 ? '' : line.slice(colon + 1);
        if (value.startsWith(' ')) value = value.slice(1);
        if (field === 'event') this.event = value;
        if (field === 'data') this.data.push(value);
        if (field === 'id' && !value.includes('\0')) this.onId(value);
        if (field === 'retry' && /^\d+$/.test(value)) this.onRetry(Number(value));
    }
}

function sleep(delay, signal) {
    return new Promise(resolve => {
        const done = () => {
            clearTimeout(timer);
            signal.removeEventListener('abort', done);
            resolve();
        };
        const timer = setTimeout(done, delay);
        signal.addEventListener('abort', done, { once: true });
        if (signal.aborted) done();
    });
}

export class MercureChannel {
    constructor({ tokenUrl = '/realtime/token', litigantToken = '', fetch: fetcher = globalThis.fetch.bind(globalThis), sleep: sleeper = sleep } = {}) {
        this.tokenUrl = tokenUrl;
        this.litigantToken = litigantToken;
        this.fetch = fetcher;
        this.sleep = sleeper;
        this.handlers = new Map();
        this.controller = new AbortController();
        this.lastEventId = '';
        this.retry = 1000;
        this.stopped = false;
    }

    bind(event, callback) {
        if (!this.handlers.has(event)) this.handlers.set(event, new Set());
        this.handlers.get(event).add(callback);
        return this;
    }

    unbind_all() {
        this.handlers.clear();
    }

    emit(event, data) {
        for (const callback of this.handlers.get(event) || []) {
            try { callback(data); } catch (error) { console.error('Realtime event handler failed', error); }
        }
    }

    start() {
        this.running ??= this.run();
        return this.running;
    }

    disconnect() {
        this.stopped = true;
        this.controller.abort();
    }

    async run() {
        while (!this.stopped) {
            try {
                const auth = await this.fetch(this.tokenUrl, {
                    method: 'POST', credentials: 'same-origin', cache: 'no-store',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify(this.litigantToken ? { litigant_token: this.litigantToken } : {}),
                    signal: this.controller.signal,
                });
                if (!auth.ok) {
                    this.emit('realtime:subscription_error', auth.status);
                    // 401/403: the session or the litigant link is no longer valid, a retry cannot
                    // fix it and would show the session-expired prompt again on every attempt.
                    if (401 === auth.status || 403 === auth.status) break;
                    throw new Error('Realtime token request failed');
                }
                const { token, hub, topics } = await auth.json();
                if (this.stopped) break;
                const query = topics.map(topic => `match=${encodeURIComponent(topic)}`).join('&');
                const headers = { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' };
                if (this.lastEventId) headers['Last-Event-ID'] = this.lastEventId;
                const response = await this.fetch(`${hub}?${query}`, { headers, cache: 'no-store', signal: this.controller.signal });
                if (!response.ok || !response.body || !response.headers.get('Content-Type')?.startsWith('text/event-stream')) {
                    throw new Error('Realtime subscription failed');
                }
                const parser = new SseParser(({ event, data }) => {
                    this.retry = 1000;
                    try { this.emit(event, JSON.parse(data)); } catch { console.error('Invalid realtime event JSON'); }
                }, id => { this.lastEventId = id; }, retry => { this.retry = Math.max(1000, Math.min(30000, retry)); });
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                try {
                    while (!this.stopped) {
                        const { value, done } = await reader.read();
                        if (done) break;
                        parser.push(decoder.decode(value, { stream: true }));
                    }
                } finally {
                    await reader.cancel().catch(() => {});
                    reader.releaseLock();
                }
            } catch (error) {
                if (this.stopped) break;
            }
            if (this.stopped) break;
            await this.sleep(this.retry, this.controller.signal);
            this.retry = Math.min(30000, this.retry * 2);
        }
    }
}

// The lawyer layout runs an inline script before any webpack code: Encore defers the runtime
// chunk, so a module export cannot be called from inline code. The layout therefore creates a
// placeholder that only queues bind() calls; this hands them to a real channel, makes the
// placeholder forward every later call, and starts the stream.
export function startFromPlaceholder(placeholder, options = {}) {
    if (!placeholder || placeholder.channel) return placeholder?.channel;
    const channel = new MercureChannel({ tokenUrl: placeholder.tokenUrl, ...options });
    for (const [event, callback] of placeholder.pending || []) channel.bind(event, callback);
    placeholder.pending = [];
    placeholder.channel = channel;
    placeholder.bind = (event, callback) => {
        channel.bind(event, callback);
        return placeholder;
    };
    placeholder.unbind_all = () => channel.unbind_all();
    placeholder.disconnect = () => channel.disconnect();
    channel.start();
    return channel;
}
